import React, { FC } from 'react';
import { useTranslation } from 'next-i18next';
import { MecImage } from '@adapters/bigcommerce/types/product';
import ImageContainer from '../ImageContainer';
import { GalleryImageFitMode } from '../constants';
import styles from '../ImageContainer/ImageContainer.module.scss';

export interface PlaceholderImageProps {
  isPriority?: boolean;
  sizes?: string;
  imageFitMode?: GalleryImageFitMode;
  children?: React.ReactNode;
}

/**
 * PlaceholderImage displays a placeholder when product images are not available.
 * Uses ImageContainer internally with a static placeholder image, so all ImageContainer
 * features (badges, tags, aspect ratios) work automatically.
 *
 * Features:
 * - Reuses ImageContainer for consistent behavior
 * - Renders "Product image coming soon" as an overlaid text child with i18n support
 * - Accepts children for overlays (e.g., Canadian badge)
 * - Matches all ImageContainer responsive behaviors
 *
 * @param isPriority - Mark image as priority for LCP optimization
 * @param sizes - Responsive sizes string for Next.js Image (defaults to first-image sizes)
 * @param children - Optional overlay content (badges, etc.)
 */
const PlaceholderImage: FC<PlaceholderImageProps> = ({
  isPriority = false,
  sizes = '(min-width: 768px) 854px, 100vw',
  imageFitMode = 'cover',
  children,
}) => {
  const { t } = useTranslation('pdp');

  // Create a MecImage object for the placeholder
  const placeholderImage: MecImage = {
    url: '/images/pdp/product-placeholder.jpg',
    urlOriginal: '/images/pdp/product-placeholder.jpg',
    altText: t('product.imageComingSoon'),
    isDefault: false,
    is_thumbnail: false,
  };

  return (
    <ImageContainer
      image={placeholderImage}
      isPriority={isPriority}
      sizes={sizes}
      imageFitMode={imageFitMode}
      omitGalleryImageInset
    >
      {children}
      <p className={styles.placeholderText}>{t('product.imageComingSoon')}</p>
    </ImageContainer>
  );
};

export default PlaceholderImage;
